import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner, faPlay, faExclamationTriangle, faCheckCircle,
  faTimesCircle, faSearch, faShieldAlt, faHeartbeat,
  faMousePointer, faKeyboard, faListAlt, faSync, faCircle,
  faDownload, faFileCode, faFileCsv, faTrash, faSave, faClock
} from '@fortawesome/free-solid-svg-icons';
import axios from '../../config/axiosConfig';

// ── Export helpers ──────────────────────────────────────────────────────────
const downloadBlob = (content, filename, mime) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

const escCsv = (v) => {
  const s = String(v ?? '');
  return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
};

const buildCsv = (diff, replayResults, baselineLabel, testLabel) => {
  const lines = [];
  lines.push('# VWG AuthZ Tester — Export');
  lines.push(`# Baseline (Admin): ${baselineLabel}`);
  lines.push(`# Test (Low-Priv): ${testLabel}`);
  lines.push(`# Admin-Only: ${diff.summary.baselineOnlyCount}, Shared: ${diff.summary.sharedCount}, Test-Only: ${diff.summary.testOnlyCount}, High-Interest: ${diff.summary.highInterest}`);
  lines.push('');

  lines.push('## Admin-Only Controls');
  lines.push(['Control ID', 'Type', 'Events', 'Label/Values', 'Replay Verdict', 'Replay Status', 'Admin Bytes', 'Low-Priv Bytes', 'New Controls', 'Admin Response (Snippet)', 'Low-Priv Response (Snippet)'].map(escCsv).join(','));
  const replayMap = {};
  if (replayResults) replayResults.results.forEach(r => { if (!replayMap[r.controlId]) replayMap[r.controlId] = r; });
  for (const c of diff.baselineOnly) {
    const r = replayMap[c.controlId];
    lines.push([
      `SR=${c.controlId}`,
      c.controlType || '',
      c.events.map(e => `${e.tp} x${e.count}`).join('; '),
      c.controlLabel || (c.txSamples.length > 0 ? c.txSamples[0].substring(0, 60) : ''),
      r?.verdict || '',
      r?.responseStatus || '',
      r?.baselineResponseLength ?? '',
      r?.responseLength ?? '',
      r?.newControlCount ?? '',
      (r?.baselineSnippet || '').substring(0, 500),
      (r?.responseSnippet || '').substring(0, 500),
    ].map(escCsv).join(','));
  }
  return lines.join('\n');
};

const escHtml = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

const buildHtmlReport = (diff, replayResults, baselineLabel, testLabel, targetName) => {
  const now = new Date().toLocaleString('de-CH');
  const allReplay = replayResults ? replayResults.results : [];
  const vulnerable = allReplay.filter(r => r.verdict === 'DATA_RETURNED' || r.verdict === 'LARGE_RESPONSE');
  const protected_ = allReplay.filter(r => r.verdict === 'IGNORED' || r.verdict === 'MINIMAL');
  const replayMap = {};
  const replayAll = {};
  if (replayResults) replayResults.results.forEach(r => {
    if (!replayMap[r.controlId]) replayMap[r.controlId] = r;
    if (!replayAll[r.controlId]) replayAll[r.controlId] = [];
    replayAll[r.controlId].push(r);
  });

  const VERDICT_DE = { DATA_RETURNED:'VERWUNDBAR', LARGE_RESPONSE:'VERDÄCHTIG', ERROR:'FEHLER', IGNORED:'GESCHÜTZT', MINIMAL:'MINIMAL', NO_RESPONSE:'KEINE ANTWORT', NO_EVENTS:'KEINE EVENTS' };
  const VERDICT_DESC = {
    DATA_RETURNED:'Server lieferte neue Controls/Daten — Zugriff auf Admin-Funktion möglich',
    LARGE_RESPONSE:'Grosse Response (>800B) — könnte sensible Daten enthalten',
    ERROR:'Server antwortete mit Fehlermeldung',
    IGNORED:'Response <150B — Server ignorierte die Anfrage (geschützt)',
    MINIMAL:'Response 150-800B, keine neuen Controls — wahrscheinlich geschützt',
    NO_RESPONSE:'Keine Antwort', NO_EVENTS:'Kein Event in Baseline'
  };
  const verdictColor = (v) => ({ DATA_RETURNED:'#dc2626', LARGE_RESPONSE:'#ea580c', ERROR:'#ca8a04', IGNORED:'#16a34a', MINIMAL:'#0d9488', NO_RESPONSE:'#6b7280', NO_EVENTS:'#6b7280' }[v] || '#6b7280');

  const snippetHtml = (text, maxLen = 800) => {
    if (!text) return '<em style="color:var(--muted)">—</em>';
    const t = text.length > maxLen ? text.substring(0, maxLen) + '…' : text;
    return `<pre class="snippet">${escHtml(t)}</pre>`;
  };

  const detailRows = (cid) => {
    const results = replayAll[cid];
    if (!results || !results.length) return '';
    return results.map(r => {
      const hasBaseline = r.baselineSnippet && r.baselineSnippet.length > 0;
      const hasLowPriv = r.responseSnippet && r.responseSnippet.length > 0;
      const sizeMatch = hasBaseline && r.responseLength != null && Math.abs(r.responseLength - r.baselineResponseLength) < 50;
      return `<tr class="detail-row">
        <td colspan="7" style="padding:8px 12px">
          <div style="font-size:11px;color:var(--muted);margin-bottom:4px"><strong>${r.eventType}</strong>${r.txValue ? ` "${escHtml(r.txValue.substring(0,40))}"` : ''} — ${VERDICT_DESC[r.verdict] || r.verdict}${sizeMatch ? ' <span style="color:#dc2626;font-weight:700">⚠ Gleiche Grösse wie Admin!</span>' : ''}</div>
          <div class="compare-grid">
            <div>
              <div class="compare-label" style="color:#ea580c">Admin (${r.baselineResponseLength != null ? r.baselineResponseLength.toLocaleString() + 'B' : '—'})</div>
              ${snippetHtml(r.baselineSnippet)}
            </div>
            <div>
              <div class="compare-label" style="color:#0d9488">Low-Priv (${r.responseLength != null ? r.responseLength.toLocaleString() + 'B' : '—'})</div>
              ${snippetHtml(r.responseSnippet)}
            </div>
          </div>
        </td>
      </tr>`;
    }).join('\n');
  };

  return `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>VWG AuthZ Report — ${escHtml(targetName || 'Target')}</title>
<style>
  :root{--bg:#fff;--fg:#111;--muted:#666;--border:#e5e7eb;--card:#f9fafb;--red:#fef2f2;--green:#f0fdf4;--teal:#0d9488;--snippet-bg:#f3f4f6;--snippet-fg:#374151}
  @media(prefers-color-scheme:dark){:root{--bg:#111827;--fg:#f3f4f6;--muted:#9ca3af;--border:#374151;--card:#1f2937;--red:#450a0a;--green:#052e16;--teal:#14b8a6;--snippet-bg:#0f172a;--snippet-fg:#94a3b8}}
  *{margin:0;box-sizing:border-box}body{font-family:system-ui,-apple-system,sans-serif;background:var(--bg);color:var(--fg);padding:32px;max-width:1200px;margin:0 auto;font-size:14px;line-height:1.5}
  h1{font-size:22px;margin-bottom:4px}h2{font-size:16px;margin:24px 0 8px;border-bottom:1px solid var(--border);padding-bottom:4px}
  .meta{color:var(--muted);font-size:12px;margin-bottom:20px}
  table{width:100%;border-collapse:collapse;font-size:12px;margin:8px 0}th,td{padding:6px 10px;text-align:left;border-bottom:1px solid var(--border)}
  th{background:var(--card);font-weight:600;color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:.5px}
  tr.vuln{background:var(--red)}tr.safe{background:var(--green)}.mono{font-family:'SF Mono',Consolas,monospace;font-size:12px}
  .summary-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin:16px 0}
  .summary-card{background:var(--card);border:1px solid var(--border);border-radius:8px;padding:16px;text-align:center}
  .summary-card .num{font-size:28px;font-weight:700}.summary-card .lbl{font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.5px}
  .verdict{padding:2px 8px;border-radius:4px;font-size:11px;font-weight:700;color:#fff;white-space:nowrap}
  .event-tag{display:inline-block;padding:1px 6px;border-radius:3px;font-size:10px;margin:1px;background:var(--card);border:1px solid var(--border)}
  .event-tag.hot{background:#fdba74;color:#7c2d12;border-color:#f97316}
  .compare-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:4px}
  .compare-label{font-size:10px;font-weight:700;margin-bottom:2px;text-transform:uppercase;letter-spacing:.5px}
  .snippet{font-family:'SF Mono',Consolas,monospace;font-size:10px;background:var(--snippet-bg);color:var(--snippet-fg);padding:6px 8px;border-radius:4px;white-space:pre-wrap;word-break:break-all;max-height:120px;overflow:auto;margin:0;border:1px solid var(--border)}
  .detail-row td{background:var(--card);border-bottom:2px solid var(--border)}
  .size-diff{font-size:10px;font-weight:700;margin-left:4px}
  .size-smaller{color:#16a34a}.size-same{color:#dc2626}
  @media print{body{padding:16px}h1{font-size:18px}table{font-size:10px}th,td{padding:4px 6px}.snippet{max-height:none;font-size:8px}.compare-grid{grid-template-columns:1fr 1fr}}
</style>
</head>
<body>
<h1>VWG Authorization Test Report</h1>
<div class="meta">
  <strong>Target:</strong> ${escHtml(targetName || '—')}<br>
  <strong>Baseline (Admin):</strong> ${escHtml(baselineLabel)}<br>
  <strong>Test (Low-Priv):</strong> ${escHtml(testLabel)}<br>
  <strong>Datum:</strong> ${now}
</div>

<h2>Zusammenfassung</h2>
<div class="summary-grid">
  <div class="summary-card"><div class="num" style="color:#ef4444">${diff.summary.baselineOnlyCount}</div><div class="lbl">Nur Admin</div></div>
  <div class="summary-card"><div class="num" style="color:#22c55e">${diff.summary.sharedCount}</div><div class="lbl">Gemeinsam</div></div>
  <div class="summary-card"><div class="num" style="color:#3b82f6">${diff.summary.testOnlyCount}</div><div class="lbl">Nur Test</div></div>
  <div class="summary-card"><div class="num" style="color:#f97316">${diff.summary.highInterest}</div><div class="lbl">High-Interest</div></div>
</div>

${replayResults ? `
<div class="summary-grid">
  <div class="summary-card" style="border-color:#ef4444"><div class="num" style="color:#ef4444">${vulnerable.length}</div><div class="lbl">Verwundbar / Verdächtig</div></div>
  <div class="summary-card" style="border-color:#22c55e"><div class="num" style="color:#22c55e">${protected_.length}</div><div class="lbl">Geschützt</div></div>
  <div class="summary-card"><div class="num">${allReplay.length}</div><div class="lbl">Total Replays</div></div>
</div>

<h2>Legende</h2>
<table style="max-width:700px">
<tbody>
${Object.entries(VERDICT_DE).map(([k,v]) => `<tr><td><span class="verdict" style="background:${verdictColor(k)}">${v}</span></td><td style="color:var(--muted)">${VERDICT_DESC[k]}</td></tr>`).join('\n')}
</tbody>
</table>
` : '<p style="color:var(--muted);font-style:italic">Kein Replay durchgeführt — nur Analyse-Ergebnisse.</p>'}

<h2>Admin-Only Controls (${diff.baselineOnly.length})</h2>
<table>
<thead><tr><th>Control</th><th>Typ</th><th>Events</th><th>Label</th>${replayResults ? '<th>Verdict</th><th>Admin</th><th>Low-Priv</th>' : ''}</tr></thead>
<tbody>
${diff.baselineOnly.map(c => {
  const r = replayMap[c.controlId];
  const isVuln = r && (r.verdict === 'DATA_RETURNED' || r.verdict === 'LARGE_RESPONSE');
  const isSafe = r && (r.verdict === 'IGNORED' || r.verdict === 'MINIMAL');
  const sizeDiff = r && r.baselineResponseLength != null && r.responseLength != null ? r.responseLength - r.baselineResponseLength : null;
  return `<tr class="${isVuln ? 'vuln' : isSafe ? 'safe' : ''}">
    <td class="mono">SR=${c.controlId}</td>
    <td>${c.controlType || '—'}</td>
    <td>${c.events.map(e => `<span class="event-tag${['Click','ValueChange','SelectionChanged'].includes(e.tp) ? ' hot' : ''}">${e.tp} x${e.count}</span>`).join(' ')}</td>
    <td>${escHtml(c.controlLabel || (c.txSamples.length > 0 ? c.txSamples[0].substring(0, 60) : '—'))}</td>
    ${r ? `<td><span class="verdict" style="background:${verdictColor(r.verdict)}">${VERDICT_DE[r.verdict] || r.verdict}</span></td>
    <td class="mono" style="text-align:right">${r.baselineResponseLength != null ? r.baselineResponseLength.toLocaleString() : '—'}</td>
    <td class="mono" style="text-align:right">${r.responseLength != null ? r.responseLength.toLocaleString() : '—'}${sizeDiff != null && sizeDiff < -500 ? '<span class="size-diff size-smaller">↓</span>' : sizeDiff != null && Math.abs(sizeDiff) < 50 ? '<span class="size-diff size-same">≈ ⚠</span>' : ''}</td>` : (replayResults ? '<td>—</td><td>—</td><td>—</td>' : '')}
  </tr>
${replayResults ? detailRows(c.controlId) : ''}`;
}).join('\n')}
</tbody>
</table>

${diff.shared && diff.shared.length > 0 ? `
<h2>Gemeinsame Controls (${diff.shared.length})${replayResults && diff.shared.some(c => replayMap[c.controlId]) ? ' — mit Vergleich' : ''}</h2>
<p style="color:var(--muted);font-size:12px;margin-bottom:8px">Diese Controls existieren in beiden Sessions. Replay-Daten zeigen, ob die Responses identisch sind oder ob der Admin mehr Daten erhält.</p>
<table>
<thead><tr><th>Control</th><th>Typ</th><th>Events</th><th>Label</th>${replayResults ? '<th>Verdict</th><th>Admin</th><th>Low-Priv</th>' : ''}</tr></thead>
<tbody>
${diff.shared.map(c => {
  const r = replayMap[c.controlId];
  const isVuln = r && (r.verdict === 'DATA_RETURNED' || r.verdict === 'LARGE_RESPONSE');
  const sizeDiff = r && r.baselineResponseLength != null && r.responseLength != null ? r.responseLength - r.baselineResponseLength : null;
  const sizeMatch = sizeDiff != null && Math.abs(sizeDiff) < 50;
  return `<tr class="${isVuln ? 'vuln' : ''}">
    <td class="mono">SR=${c.controlId}</td>
    <td>${c.controlType || '—'}</td>
    <td>${c.events.map(e => `<span class="event-tag${['Click','ValueChange','SelectionChanged'].includes(e.tp) ? ' hot' : ''}">${e.tp} x${e.count}</span>`).join(' ')}</td>
    <td>${escHtml(c.controlLabel || (c.txSamples?.length > 0 ? c.txSamples[0].substring(0, 60) : '—'))}</td>
    ${r ? `<td><span class="verdict" style="background:${verdictColor(r.verdict)}">${VERDICT_DE[r.verdict] || r.verdict}</span></td>
    <td class="mono" style="text-align:right">${r.baselineResponseLength != null ? r.baselineResponseLength.toLocaleString() : '—'}</td>
    <td class="mono" style="text-align:right">${r.responseLength != null ? r.responseLength.toLocaleString() : '—'}${sizeMatch ? '<span class="size-diff size-same">≈</span>' : sizeDiff != null && sizeDiff < -500 ? '<span class="size-diff size-smaller">↓</span>' : ''}</td>` : (replayResults ? '<td>—</td><td>—</td><td>—</td>' : '')}
  </tr>
${replayResults ? detailRows(c.controlId) : ''}`;
}).join('\n')}
</tbody>
</table>
` : ''}

${diff.testOnly && diff.testOnly.length > 0 ? `
<h2>Nur Test-Session (${diff.testOnly.length})</h2>
<table>
<thead><tr><th>Control</th><th>Typ</th><th>Events</th></tr></thead>
<tbody>
${diff.testOnly.map(c => `<tr><td class="mono">SR=${c.controlId}</td><td>${c.controlType || '—'}</td><td>${c.events.map(e => `<span class="event-tag">${e.tp} x${e.count}</span>`).join(' ')}</td></tr>`).join('\n')}
</tbody>
</table>
` : ''}

<div style="margin-top:32px;padding-top:12px;border-top:1px solid var(--border);color:var(--muted);font-size:11px">
  PurpleHQ VWG AuthZ Tester — Report generiert am ${now}
</div>
</body></html>`;
};

// Simple line-based diff: splits VWG XML on control boundaries and highlights differences
const computeVwgDiff = (admin, lowpriv) => {
  if (!admin || !lowpriv) return null;
  // Split on VWG control tags for meaningful chunks
  const split = (s) => s.replace(/></g, '>\n<').split('\n').filter(l => l.trim());
  const aLines = split(admin);
  const bLines = split(lowpriv);
  const aSet = new Set(aLines);
  const bSet = new Set(bLines);
  const result = [];
  // Lines only in admin
  for (const l of aLines) {
    if (!bSet.has(l)) result.push({ type: 'admin-only', text: l });
  }
  // Lines only in low-priv
  for (const l of bLines) {
    if (!aSet.has(l)) result.push({ type: 'lowpriv-only', text: l });
  }
  // Shared count
  const shared = aLines.filter(l => bSet.has(l)).length;
  return { adminOnly: result.filter(r => r.type === 'admin-only'), lowprivOnly: result.filter(r => r.type === 'lowpriv-only'), shared, totalAdmin: aLines.length, totalLowpriv: bLines.length };
};

const EVENT_ICONS = {
  Click: faMousePointer,
  ValueChange: faKeyboard,
  SelectionChanged: faListAlt,
};

const VERDICT_COLORS = {
  DATA_RETURNED: { bg: 'bg-red-600', text: 'text-white', label: 'VERWUNDBAR', desc: 'Server lieferte neue Controls/Daten — Zugriff auf Admin-Funktion möglich' },
  LARGE_RESPONSE: { bg: 'bg-orange-500', text: 'text-white', label: 'VERDÄCHTIG', desc: 'Grosse Response (>800B) — könnte sensible Daten enthalten' },
  ERROR: { bg: 'bg-yellow-600', text: 'text-white', label: 'FEHLER', desc: 'Server antwortete mit Fehlermeldung — ggf. geschützt, aber Error Disclosure' },
  IGNORED: { bg: 'bg-green-600', text: 'text-white', label: 'GESCHÜTZT', desc: 'Response <150B — Server ignorierte die Anfrage (kein Zugriff)' },
  MINIMAL: { bg: 'bg-teal-600', text: 'text-white', label: 'MINIMAL', desc: 'Response 150-800B, keine neuen Controls — wahrscheinlich geschützt, aber Response prüfen' },
  NO_RESPONSE: { bg: 'bg-gray-500', text: 'text-white', label: 'KEINE ANTWORT', desc: 'Server antwortete nicht oder leere Response' },
  NO_EVENTS: { bg: 'bg-gray-500', text: 'text-white', label: 'KEINE EVENTS', desc: 'Kein Baseline-Event gefunden für dieses Control' },
};

const SessionStatusDot = ({ status, isDarkMode }) => {
  if (status === 'checking') return <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400 text-[10px]" />;
  if (status === 'alive') return <FontAwesomeIcon icon={faCircle} className="text-green-500 text-[8px]" />;
  if (status === 'expired') return <FontAwesomeIcon icon={faCircle} className="text-red-500 text-[8px]" />;
  return <FontAwesomeIcon icon={faCircle} className="text-gray-500 text-[8px]" />;
};

const VwgAuthzTester = ({ webTarget, isDarkMode }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [baselineVwg, setBaselineVwg] = useState('');
  const [testVwg, setTestVwg] = useState('');
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [replayResults, setReplayResults] = useState(null);
  const [replayLoading, setReplayLoading] = useState(false);
  const [selectedControls, setSelectedControls] = useState(new Set());
  const [error, setError] = useState('');

  // Session status tracking
  const [sessionStatus, setSessionStatus] = useState({}); // vwg → 'alive'|'expired'|'checking'
  const [keepAliveActive, setKeepAliveActive] = useState(new Set());
  const keepAliveIntervals = useRef({});

  // Saved analyses
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [activeAnalysisId, setActiveAnalysisId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const replayResultsRef = useRef(null);

  // Replay result search/filter
  const [replaySearch, setReplaySearch] = useState('');
  const [replayVerdictFilter, setReplayVerdictFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Admin response search
  const [adminSearch, setAdminSearch] = useState('');
  const [adminSearchResults, setAdminSearchResults] = useState(null);
  const [adminSearchLoading, setAdminSearchLoading] = useState(false);

  // Control scope filter: 'admin-only' | 'shared' | 'all'
  const [controlScope, setControlScope] = useState('admin-only');

  const cardBg = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  const wtId = webTarget?._id;

  const loadSessions = useCallback(async () => {
    if (!wtId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`/vwg-authz/${wtId}/sessions`);
      setSessions(data);
      // Auto-probe all sessions in background
      for (const s of data) {
        setSessionStatus(prev => ({ ...prev, [s.vwginstance]: 'checking' }));
        axios.get(`/vwg-authz/${wtId}/probe/${s.vwginstance}`)
          .then(r => setSessionStatus(prev => ({ ...prev, [s.vwginstance]: r.data.alive ? 'alive' : 'expired' })))
          .catch(() => setSessionStatus(prev => ({ ...prev, [s.vwginstance]: 'expired' })));
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [wtId]);

  const loadAnalyses = useCallback(async () => {
    if (!wtId) return;
    try {
      const { data } = await axios.get(`/vwg-authz/${wtId}/analyses`);
      setSavedAnalyses(data);
    } catch { /* silent */ }
  }, [wtId]);

  useEffect(() => { loadSessions(); loadAnalyses(); }, [loadSessions, loadAnalyses]);

  // Cleanup keep-alive intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(keepAliveIntervals.current).forEach(clearInterval);
    };
  }, []);

  const probeSession = async (vwg) => {
    setSessionStatus(prev => ({ ...prev, [vwg]: 'checking' }));
    try {
      const { data } = await axios.get(`/vwg-authz/${wtId}/probe/${vwg}`);
      setSessionStatus(prev => ({ ...prev, [vwg]: data.alive ? 'alive' : 'expired' }));
      return data;
    } catch {
      setSessionStatus(prev => ({ ...prev, [vwg]: 'expired' }));
      return { alive: false };
    }
  };

  const probeAll = async () => {
    const vwgs = [...new Set([baselineVwg, testVwg].filter(Boolean))];
    await Promise.all(vwgs.map(probeSession));
  };

  const toggleKeepAlive = (vwg) => {
    setKeepAliveActive(prev => {
      const next = new Set(prev);
      if (next.has(vwg)) {
        next.delete(vwg);
        if (keepAliveIntervals.current[vwg]) {
          clearInterval(keepAliveIntervals.current[vwg]);
          delete keepAliveIntervals.current[vwg];
        }
      } else {
        next.add(vwg);
        const send = async () => {
          try {
            const { data } = await axios.post(`/vwg-authz/${wtId}/keepalive/${vwg}`);
            setSessionStatus(p => ({ ...p, [vwg]: data.alive ? 'alive' : 'expired' }));
            if (!data.alive) {
              clearInterval(keepAliveIntervals.current[vwg]);
              delete keepAliveIntervals.current[vwg];
              setKeepAliveActive(p => { const n = new Set(p); n.delete(vwg); return n; });
            }
          } catch {
            setSessionStatus(p => ({ ...p, [vwg]: 'expired' }));
          }
        };
        send();
        keepAliveIntervals.current[vwg] = setInterval(send, 60000);
      }
      return next;
    });
  };

  const findNewSession = async () => {
    setError('');
    try {
      const { data } = await axios.get(`/vwg-authz/${wtId}/newest-session`, {
        params: { exclude: baselineVwg }
      });
      if (data.alive) {
        await loadSessions();
        setTestVwg(data.vwginstance);
        setSessionStatus(prev => ({ ...prev, [data.vwginstance]: 'alive' }));
      } else {
        setError('Keine aktive neue Session gefunden. Logge dich mit dem Low-Priv-User über den Proxy ein und versuche es nochmals.');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const runDiff = async () => {
    if (!baselineVwg || !testVwg) return;
    setDiffLoading(true);
    setReplayResults(null);
    setError('');
    try {
      const { data } = await axios.post(`/vwg-authz/${wtId}/diff`, { baselineVwg, testVwg });
      setDiff(data);
      const autoSelect = new Set(data.baselineOnly.map(c => c.controlId));
      setSelectedControls(autoSelect);
      // Auto-save analysis
      const bl = sessions.find(s => s.vwginstance === baselineVwg);
      const tl = sessions.find(s => s.vwginstance === testVwg);
      try {
        const { data: saved } = await axios.post(`/vwg-authz/${wtId}/analyses`, {
          baselineVwg, testVwg,
          baselineLabel: bl ? sessionLabel(bl) : baselineVwg,
          testLabel: tl ? sessionLabel(tl) : testVwg,
          diff: data,
          selectedControlIds: Array.from(autoSelect),
        });
        setActiveAnalysisId(saved._id);
        loadAnalyses();
      } catch { /* save is best-effort */ }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setDiffLoading(false);
    }
  };

  const runReplay = async () => {
    if (!selectedControls.size) return;
    const testSession = sessions.find(s => s.vwginstance === testVwg);
    if (!testSession) return;
    setReplayLoading(true);
    setReplayResults(null);
    setError('');
    try {
      const { data } = await axios.post(`/vwg-authz/${wtId}/replay`, {
        baselineVwg,
        targetCookie: testSession.cookie,
        targetVwginstance: testVwg,
        controlIds: Array.from(selectedControls),
      });
      setReplayResults(data);
      setTimeout(() => replayResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      // Save replay results to active analysis
      if (activeAnalysisId) {
        try {
          await axios.put(`/vwg-authz/${wtId}/analyses/${activeAnalysisId}`, {
            replayResults: data,
            selectedControlIds: Array.from(selectedControls),
          });
          loadAnalyses();
        } catch { /* best-effort */ }
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setReplayLoading(false);
    }
  };

  const loadSavedAnalysis = async (analysis) => {
    setActiveAnalysisId(analysis._id);
    setBaselineVwg(analysis.baselineVwg);
    setTestVwg(analysis.testVwg);
    setReplaySearch('');
    setReplayVerdictFilter('all');
    setExpandedRows(new Set());
    setError('');
    setAnalysisLoading(true);
    setDiff(null);
    setReplayResults(null);

    try {
      const { data: full } = await axios.get(`/vwg-authz/${wtId}/analyses/${analysis._id}`);
      setDiff(full.diff);
      setReplayResults(full.replayResults || null);
      setSelectedControls(new Set(full.selectedControlIds || []));
    } catch (err) {
      setError('Analyse konnte nicht geladen werden: ' + (err.response?.data?.error || err.message));
    } finally {
      setAnalysisLoading(false);
    }
  };

  const deleteSavedAnalysis = async (id) => {
    try {
      await axios.delete(`/vwg-authz/${wtId}/analyses/${id}`);
      setSavedAnalyses(prev => prev.filter(a => a._id !== id));
      if (activeAnalysisId === id) {
        setActiveAnalysisId(null);
        setDiff(null);
        setReplayResults(null);
        setSelectedControls(new Set());
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const toggleControl = (cid) => {
    setSelectedControls(prev => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };

  const selectAll = () => {
    if (!diff) return;
    const controls = controlScope === 'admin-only' ? diff.baselineOnly
      : controlScope === 'shared' ? (diff.shared || [])
      : [...diff.baselineOnly, ...(diff.shared || [])];
    setSelectedControls(new Set(controls.map(c => c.controlId)));
  };

  const selectNone = () => setSelectedControls(new Set());

  const searchAdminResponses = async (query) => {
    if (!query || query.length < 2 || !baselineVwg) return;
    setAdminSearchLoading(true);
    try {
      const { data } = await axios.post(`/vwg-authz/${wtId}/search-responses`, {
        vwginstance: baselineVwg,
        query,
        limit: 30,
      });
      setAdminSearchResults(data);
      // Auto-select matching control IDs
      if (data.results?.length) {
        const matchIds = new Set(data.results.flatMap(r => r.controlIds));
        setSelectedControls(prev => {
          const next = new Set(prev);
          for (const id of matchIds) next.add(id);
          return next;
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setAdminSearchLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl text-teal-500" />
      </div>
    );
  }

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  };

  const sessionLabel = (s, includeStatus) => {
    const name = s.profileName ? s.profileName.replace(/^Auto:\s*/i, '') : s.vwginstance.substring(0, 8) + '…';
    const status = sessionStatus[s.vwginstance];
    const prefix = includeStatus ? (status === 'alive' ? '● ' : status === 'expired' ? '✗ ' : status === 'checking' ? '… ' : '? ') : '';
    return `${prefix}${name} | ${s.controlCount ?? '?'} Ctrls | ${s.exchangeCount} Req | ${formatTime(s.firstSeen)}–${formatTime(s.lastSeen)}`;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className={`p-4 rounded-lg border ${cardBg}`}>
        <h3 className={`text-lg font-bold mb-1 ${textPrimary}`}>
          <FontAwesomeIcon icon={faShieldAlt} className="mr-2 text-teal-500" />
          VWG AuthZ Tester
        </h3>
        <p className={`text-sm ${textSecondary}`}>
          Vergleicht VWG-Control-Events zwischen Rollen und replayed Admin-Events durch Low-Priv-Sessions.
        </p>
      </div>

      {error && (
        <div className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-red-900/30 border border-red-700 text-red-300' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {error}
        </div>
      )}

      {/* Session Selector */}
      {sessions.length === 0 && !loading ? (
        <div className={`p-6 text-center rounded-lg border ${cardBg}`}>
          <p className={textSecondary}>Keine VWG-Sessions gefunden. Starte den Proxy und browse die App.</p>
        </div>
      ) : (
        <div className={`p-4 rounded-lg border ${cardBg}`}>
          <div className="flex items-center justify-between mb-3">
            <h4 className={`text-sm font-bold ${textPrimary}`}>Sessions auswählen</h4>
            <div className="flex gap-2 items-center">
              <button
                onClick={() => {
                  const allAlive = sessions.filter(s => sessionStatus[s.vwginstance] === 'alive').map(s => s.vwginstance);
                  const anyActive = allAlive.some(v => keepAliveActive.has(v));
                  if (anyActive) {
                    allAlive.forEach(v => { if (keepAliveActive.has(v)) toggleKeepAlive(v); });
                  } else {
                    allAlive.forEach(v => { if (!keepAliveActive.has(v)) toggleKeepAlive(v); });
                  }
                }}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                  keepAliveActive.size > 0
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={keepAliveActive.size > 0
                  ? `Keep-Alive aktiv für ${keepAliveActive.size} Session(s) — Klick zum Stoppen`
                  : 'Hält alle aktiven Sessions am Leben (Timer-Event alle 60s)'
                }
              >
                <FontAwesomeIcon icon={faHeartbeat} />
                {keepAliveActive.size > 0 ? `Keep-Alive (${keepAliveActive.size})` : 'Keep-Alive'}
              </button>
              <button
                onClick={probeAll}
                disabled={!baselineVwg && !testVwg}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} disabled:opacity-50`}
              >
                <FontAwesomeIcon icon={faHeartbeat} /> Status prüfen
              </button>
              <button
                onClick={loadSessions}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                <FontAwesomeIcon icon={faSync} /> Aktualisieren
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Baseline (Admin) */}
            <div>
              <label className={`block text-xs font-semibold mb-1 ${textSecondary}`}>
                Baseline (Admin / High-Priv)
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={baselineVwg}
                  onChange={e => { setBaselineVwg(e.target.value); setDiff(null); setReplayResults(null); }}
                  className={`flex-1 px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                >
                  <option value="">— Session wählen —</option>
                  {sessions.map(s => (
                    <option key={s.vwginstance} value={s.vwginstance}>
                      {sessionLabel(s, true)}
                    </option>
                  ))}
                </select>
                {baselineVwg && <SessionStatusDot status={sessionStatus[baselineVwg]} isDarkMode={isDarkMode} />}
              </div>
              {sessionStatus[baselineVwg] === 'expired' && baselineVwg && (
                <span className="text-[10px] text-red-400 mt-1 block">Session abgelaufen — Analyse funktioniert trotzdem (historische Daten)</span>
              )}
            </div>

            {/* Test (Low-Priv) */}
            <div>
              <label className={`block text-xs font-semibold mb-1 ${textSecondary}`}>
                Test (Low-Priv / Andere Rolle)
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={testVwg}
                  onChange={e => { setTestVwg(e.target.value); setReplayResults(null); }}
                  className={`flex-1 px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                >
                  <option value="">— Session wählen —</option>
                  {sessions.map(s => (
                    <option key={s.vwginstance} value={s.vwginstance}>
                      {sessionLabel(s, true)}
                    </option>
                  ))}
                </select>
                {testVwg && <SessionStatusDot status={sessionStatus[testVwg]} isDarkMode={isDarkMode} />}
              </div>
              <div className="mt-1 flex items-center gap-2">
                {sessionStatus[testVwg] === 'expired' && (
                  <button
                    onClick={findNewSession}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-orange-600 text-white hover:bg-orange-700"
                  >
                    <FontAwesomeIcon icon={faSync} className="mr-1" />
                    Neue Session nach Re-Login suchen
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={runDiff}
              disabled={!baselineVwg || !testVwg || baselineVwg === testVwg || diffLoading}
              className="bg-teal-600 text-white px-4 py-2 rounded text-sm hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
            >
              {diffLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : <FontAwesomeIcon icon={faSearch} />}
              Analysieren
            </button>
            {baselineVwg === testVwg && baselineVwg && (
              <span className={`text-xs ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                Baseline und Test müssen verschieden sein
              </span>
            )}
          </div>
        </div>
      )}

      {/* Saved Analyses Tabs */}
      {savedAnalyses.length > 0 && (
        <div className={`rounded-lg border ${cardBg}`}>
          <div className="flex items-center overflow-x-auto">
            {savedAnalyses.map(a => {
              const isActive = activeAnalysisId === a._id;
              const ts = new Date(a.createdAt).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
              const hasReplay = a.hasReplayResults || !!a.replayResults;
              const blUser = (a.baselineLabel || '').split('|')[0]?.trim()?.split('@')[0] || 'Admin';
              const tlUser = (a.testLabel || '').split('|')[0]?.trim()?.split('@')[0] || 'Test';
              return (
                <div
                  key={a._id}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs cursor-pointer border-b-2 whitespace-nowrap ${
                    isActive
                      ? 'border-teal-500 ' + (isDarkMode ? 'text-teal-400 bg-gray-700/50' : 'text-teal-700 bg-teal-50')
                      : 'border-transparent ' + (isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/30' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50')
                  }`}
                  onClick={() => loadSavedAnalysis(a)}
                  title={`Baseline: ${a.baselineLabel || '?'}\nTest: ${a.testLabel || '?'}`}
                >
                  <FontAwesomeIcon icon={faClock} className="text-[9px]" />
                  <span>{ts}</span>
                  <span className={`text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{blUser} vs {tlUser}</span>
                  {hasReplay && <span className={`text-[9px] px-1 rounded ${isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}>Replay</span>}
                  <span className={`text-[9px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                    {a.diff?.summary?.baselineOnlyCount || '?'} Admin-Only
                  </span>
                  <button
                    onClick={e => { e.stopPropagation(); deleteSavedAnalysis(a._id); }}
                    className={`ml-1 text-[9px] px-1 rounded hover:bg-red-900/30 ${isDarkMode ? 'text-gray-600 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}
                    title="Analyse löschen"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Loading indicator for saved analysis */}
      {analysisLoading && !diff && (
        <div className={`p-6 rounded-lg border ${cardBg} flex items-center justify-center gap-3`}>
          <FontAwesomeIcon icon={faSpinner} className="animate-spin text-teal-500 text-lg" />
          <span className={textSecondary}>Lade gespeicherte Analyse...</span>
        </div>
      )}

      {/* Diff Results */}
      {diff && (
        <div className={`p-4 rounded-lg border ${cardBg}`}>
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            <h4 className={`text-sm font-bold ${textPrimary}`}>Ergebnis</h4>
            {(() => {
              const bl = sessions.find(s => s.vwginstance === baselineVwg);
              const tl = sessions.find(s => s.vwginstance === testVwg);
              const blName = bl?.profileName ? bl.profileName.replace(/^Auto:\s*/i, '').split('@')[0] : baselineVwg.substring(0, 8);
              const tlName = tl?.profileName ? tl.profileName.replace(/^Auto:\s*/i, '').split('@')[0] : testVwg.substring(0, 8);
              return (
                <span className={`text-[10px] ${textSecondary}`}>
                  <span className={isDarkMode ? 'text-red-400' : 'text-red-600'}>{blName}</span>
                  {' vs '}
                  <span className={isDarkMode ? 'text-blue-400' : 'text-blue-600'}>{tlName}</span>
                </span>
              );
            })()}
            <div className="flex gap-4 text-xs flex-wrap">
              <span className={`px-2 py-1 rounded ${isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-700'}`}>
                Nur Admin: {diff.summary.baselineOnlyCount}
              </span>
              <span className={`px-2 py-1 rounded ${isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-50 text-green-700'}`}>
                Gemeinsam: {diff.summary.sharedCount}
              </span>
              <span className={`px-2 py-1 rounded ${isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                Nur Test: {diff.summary.testOnlyCount}
              </span>
              <span className={`px-2 py-1 rounded font-semibold ${isDarkMode ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-50 text-orange-700'}`}>
                High-Interest: {diff.summary.highInterest}
              </span>
            </div>
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => {
                  const bl = sessions.find(s => s.vwginstance === baselineVwg);
                  const tl = sessions.find(s => s.vwginstance === testVwg);
                  const csv = buildCsv(diff, replayResults, bl ? sessionLabel(bl) : baselineVwg, tl ? sessionLabel(tl) : testVwg);
                  downloadBlob(csv, `vwg-authz-${webTarget?.name || 'export'}.csv`, 'text/csv;charset=utf-8');
                }}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                title="Export als CSV"
              >
                <FontAwesomeIcon icon={faFileCsv} /> CSV
              </button>
              <button
                onClick={() => {
                  const bl = sessions.find(s => s.vwginstance === baselineVwg);
                  const tl = sessions.find(s => s.vwginstance === testVwg);
                  const html = buildHtmlReport(diff, replayResults, bl ? sessionLabel(bl) : baselineVwg, tl ? sessionLabel(tl) : testVwg, webTarget?.name || webTarget?.baseUrl);
                  downloadBlob(html, `vwg-authz-report-${webTarget?.name || 'export'}.html`, 'text/html;charset=utf-8');
                }}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                title="Export als HTML Report"
              >
                <FontAwesomeIcon icon={faFileCode} /> HTML
              </button>
            </div>
          </div>

          {diff && (
            <>
              {/* Scope toggle */}
              <div className="flex items-center gap-1 mb-2">
                {[
                  { key: 'admin-only', label: 'Nur Admin', count: diff.baselineOnly.length, color: 'red' },
                  { key: 'shared', label: 'Gemeinsam', count: (diff.shared || []).length, color: 'blue' },
                  { key: 'all', label: 'Alle', count: diff.baselineOnly.length + (diff.shared || []).length, color: 'gray' },
                ].map(s => (
                  <button
                    key={s.key}
                    onClick={() => {
                      setControlScope(s.key);
                      const controls = s.key === 'admin-only' ? diff.baselineOnly
                        : s.key === 'shared' ? (diff.shared || [])
                        : [...diff.baselineOnly, ...(diff.shared || [])];
                      setSelectedControls(new Set(controls.map(c => c.controlId)));
                    }}
                    className={`text-[11px] px-2.5 py-1 rounded font-medium ${
                      controlScope === s.key
                        ? s.color === 'red' ? 'bg-red-600 text-white'
                          : s.color === 'blue' ? 'bg-blue-600 text-white'
                          : 'bg-gray-600 text-white'
                        : isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {s.label} ({s.count})
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <h5 className={`text-xs font-semibold ${textSecondary}`}>
                  {controlScope === 'admin-only' ? 'Admin-Only' : controlScope === 'shared' ? 'Gemeinsame' : 'Alle'} Controls ({
                    (controlScope === 'admin-only' ? diff.baselineOnly : controlScope === 'shared' ? (diff.shared || []) : [...diff.baselineOnly, ...(diff.shared || [])]).length
                  } — {selectedControls.size} selektiert)
                </h5>
                <div className="flex gap-2 items-center">
                  <div className="relative">
                    <FontAwesomeIcon icon={faSearch} className={`absolute left-2 top-1/2 -translate-y-1/2 text-[10px] ${textSecondary}`} />
                    <input
                      type="text"
                      value={adminSearch}
                      onChange={e => setAdminSearch(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && searchAdminResponses(adminSearch)}
                      placeholder="Suche in Admin-Responses (z.B. Benutzername)..."
                      className={`pl-6 pr-2 py-1 text-xs rounded border w-64 ${borderColor} ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}
                    />
                  </div>
                  <button
                    onClick={() => searchAdminResponses(adminSearch)}
                    disabled={!adminSearch || adminSearch.length < 2 || adminSearchLoading}
                    className="text-xs px-2 py-1 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
                  >
                    {adminSearchLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Suchen'}
                  </button>
                  <button onClick={selectAll} className="text-xs text-teal-500 hover:underline">Alle</button>
                  <button onClick={selectNone} className="text-xs text-teal-500 hover:underline">Keine</button>
                </div>
              </div>

              {/* Admin Search Results */}
              {adminSearchResults && (
                <div className={`mb-3 p-3 rounded border ${isDarkMode ? 'bg-gray-900 border-gray-600' : 'bg-yellow-50 border-yellow-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs font-bold ${textPrimary}`}>
                      <FontAwesomeIcon icon={faSearch} className="mr-1" />
                      {adminSearchResults.total} Treffer für "{adminSearch}" in Admin-Responses
                    </span>
                    <div className="flex items-center gap-2">
                      {adminSearchResults.results.length > 0 && (
                        <button
                          onClick={() => {
                            const matchIds = new Set(adminSearchResults.results.flatMap(r => r.controlIds));
                            setSelectedControls(matchIds);
                          }}
                          className="text-xs px-2 py-0.5 rounded bg-orange-600 text-white hover:bg-orange-700 font-bold"
                        >
                          Nur Treffer ({new Set(adminSearchResults.results.flatMap(r => r.controlIds)).size})
                        </button>
                      )}
                      <button
                        onClick={() => { setAdminSearchResults(null); setAdminSearch(''); }}
                        className={`text-xs ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                      >✕ Schliessen</button>
                    </div>
                  </div>
                  {adminSearchResults.results.length === 0 ? (
                    <span className={`text-xs ${textSecondary}`}>Kein Treffer — versuche einen anderen Suchbegriff</span>
                  ) : (
                    <div className={`max-h-48 overflow-y-auto rounded border text-xs ${borderColor}`}>
                      {adminSearchResults.results.map((r, i) => (
                        <div key={i} className={`p-2 border-t ${borderColor} first:border-t-0`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`font-mono font-bold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                              {r.controlIds.map(id => `SR=${id}`).join(', ')}
                            </span>
                            <span className={textSecondary}>{r.events.map(e => e.tp).join(', ')}</span>
                            <span className={`${textSecondary} text-[10px]`}>{r.responseLength.toLocaleString()}B</span>
                            <button
                              onClick={() => {
                                setSelectedControls(prev => {
                                  const next = new Set(prev);
                                  r.controlIds.forEach(id => next.has(id) ? next.delete(id) : next.add(id));
                                  return next;
                                });
                              }}
                              className={`text-[10px] px-1.5 py-0.5 rounded ${
                                r.controlIds.every(id => selectedControls.has(id))
                                  ? 'bg-teal-600 text-white'
                                  : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'
                              }`}
                            >
                              {r.controlIds.every(id => selectedControls.has(id)) ? '✓ Selektiert' : '+ Selektieren'}
                            </button>
                          </div>
                          <pre className={`text-[10px] font-mono whitespace-pre-wrap break-all max-h-20 overflow-auto p-1 rounded ${isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-600'}`}>
                            {r.snippet}
                          </pre>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className={`max-h-80 overflow-y-auto rounded border ${borderColor}`}>
                <table className="w-full text-xs">
                  <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                    <tr>
                      <th className="px-2 py-1.5 text-left w-8"></th>
                      <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Control ID</th>
                      <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Typ</th>
                      <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Events</th>
                      <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Label/Werte</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(controlScope === 'admin-only' ? diff.baselineOnly
                      : controlScope === 'shared' ? (diff.shared || [])
                      : [...diff.baselineOnly, ...(diff.shared || [])]
                    ).map(c => {
                      const hasInteresting = c.events.some(e =>
                        ['Click', 'ValueChange', 'SelectionChanged'].includes(e.tp)
                      );
                      const isShared = (diff.shared || []).some(s => s.controlId === c.controlId);
                      return (
                        <tr
                          key={c.controlId}
                          onClick={() => toggleControl(c.controlId)}
                          className={`cursor-pointer border-t ${borderColor} ${
                            selectedControls.has(c.controlId)
                              ? isDarkMode ? 'bg-teal-900/30' : 'bg-teal-50'
                              : isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="px-2 py-1.5 text-center">
                            <input
                              type="checkbox"
                              checked={selectedControls.has(c.controlId)}
                              onChange={() => toggleControl(c.controlId)}
                              className="rounded"
                            />
                          </td>
                          <td className={`px-2 py-1.5 font-mono ${hasInteresting ? 'font-bold text-orange-400' : textPrimary}`}>
                            SR={c.controlId}
                            {isShared && controlScope === 'all' && <span className="ml-1 text-[9px] text-blue-400">●</span>}
                          </td>
                          <td className={`px-2 py-1.5 ${textSecondary}`}>
                            {c.controlType || '—'}
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="flex gap-1 flex-wrap">
                              {c.events.map(e => (
                                <span
                                  key={e.tp}
                                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                                    ['Click', 'ValueChange', 'SelectionChanged'].includes(e.tp)
                                      ? isDarkMode ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-100 text-orange-700'
                                      : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                                  }`}
                                >
                                  {EVENT_ICONS[e.tp] && <FontAwesomeIcon icon={EVENT_ICONS[e.tp]} className="text-[9px]" />}
                                  {e.tp} x{e.count}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className={`px-2 py-1.5 ${textSecondary} max-w-48 truncate`}>
                            {c.controlLabel || (c.txSamples.length > 0 ? c.txSamples[0].substring(0, 40) : '—')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Replay Button */}
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <button
                  onClick={runReplay}
                  disabled={!selectedControls.size || replayLoading}
                  className="bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {replayLoading
                    ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    : <FontAwesomeIcon icon={faPlay} />
                  }
                  Replay {selectedControls.size} Controls{controlScope === 'shared' ? ' (Vergleich Admin vs Low-Priv)' : ' durch Low-Priv-Session'}
                </button>
                {sessionStatus[testVwg] === 'expired' && (
                  <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'}`}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                    Test-Session abgelaufen — Replay wird fehlschlagen. Neu einloggen und "Neue Session suchen".
                  </span>
                )}
                {sessionStatus[testVwg] !== 'expired' && !replayResults && (
                  <span className={`text-xs ${textSecondary}`}>
                    Sendet Admin-Events mit den Credentials der Test-Session
                  </span>
                )}
              </div>

              {/* Replay Results (inline, right after button) */}
              {analysisLoading && (
                <div className="mt-4 flex items-center gap-2 text-sm">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-teal-500" />
                  <span className={textSecondary}>Lade Replay-Ergebnisse...</span>
                </div>
              )}
              {replayResults && (replayResults.results || []).length > 0 && (() => {
                const allResults = replayResults.results;
                const searchLower = replaySearch.toLowerCase();
                const filtered = allResults.filter(r => {
                  if (replayVerdictFilter !== 'all' && r.verdict !== replayVerdictFilter) return false;
                  if (searchLower) {
                    const haystack = [
                      `SR=${r.controlId}`, r.eventType, r.txValue,
                      r.responseSnippet, r.baselineSnippet, r.verdict,
                    ].filter(Boolean).join(' ').toLowerCase();
                    return haystack.includes(searchLower);
                  }
                  return true;
                });
                const vulnerable = allResults.filter(r => r.verdict === 'DATA_RETURNED' || r.verdict === 'LARGE_RESPONSE');
                const protected_ = allResults.filter(r => r.verdict === 'IGNORED' || r.verdict === 'MINIMAL');
                const verdictCounts = {};
                for (const r of allResults) verdictCounts[r.verdict] = (verdictCounts[r.verdict] || 0) + 1;

                return (
                  <div ref={replayResultsRef} className="mt-4">
                    {/* Summary badges */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h5 className={`text-xs font-bold ${textPrimary}`}>Replay-Ergebnisse ({allResults.length})</h5>
                      {vulnerable.length > 0 && (
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'}`}>
                          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                          {vulnerable.length} verwundbar
                        </span>
                      )}
                      {protected_.length > 0 && (
                        <span className={`px-2 py-0.5 rounded text-[10px] ${isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}>
                          <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                          {protected_.length} geschützt
                        </span>
                      )}
                    </div>

                    {/* Search & filter bar */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <div className="relative flex-1 min-w-48">
                        <FontAwesomeIcon icon={faSearch} className={`absolute left-2 top-1/2 -translate-y-1/2 text-[10px] ${textSecondary}`} />
                        <input
                          type="text"
                          value={replaySearch}
                          onChange={e => setReplaySearch(e.target.value)}
                          placeholder="Suche in Controls, Events, Response-Snippets..."
                          className={`w-full pl-6 pr-2 py-1 text-xs rounded border ${borderColor} ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}
                        />
                      </div>
                      <select
                        value={replayVerdictFilter}
                        onChange={e => setReplayVerdictFilter(e.target.value)}
                        className={`text-xs px-2 py-1 rounded border ${borderColor} ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}
                      >
                        <option value="all">Alle Verdicts ({allResults.length})</option>
                        {Object.entries(verdictCounts).sort((a,b) => b[1] - a[1]).map(([v, cnt]) => {
                          const vc = VERDICT_COLORS[v];
                          return <option key={v} value={v}>{vc?.label || v} ({cnt})</option>;
                        })}
                      </select>
                      {(replaySearch || replayVerdictFilter !== 'all') && (
                        <span className={`text-[10px] ${textSecondary}`}>{filtered.length} von {allResults.length}</span>
                      )}
                    </div>

                    {/* Results table with scroll */}
                    <div className={`max-h-96 overflow-y-auto rounded border ${borderColor}`}>
                      <table className="w-full text-xs">
                        <thead className={`sticky top-0 z-10 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                          <tr>
                            <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Control</th>
                            <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Event</th>
                            <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Status</th>
                            <th className={`px-2 py-1.5 text-right ${textSecondary}`}>Admin</th>
                            <th className={`px-2 py-1.5 text-right ${textSecondary}`}>Low-Priv</th>
                            <th className={`px-2 py-1.5 text-right ${textSecondary}`}>Neue Ctrls</th>
                            <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Verdict</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((r, i) => {
                            const vd = VERDICT_COLORS[r.verdict] || VERDICT_COLORS.NO_RESPONSE;
                            const isVulnerable = r.verdict === 'DATA_RETURNED' || r.verdict === 'LARGE_RESPONSE';
                            const rowKey = `${r.controlId}-${r.eventType}-${i}`;
                            const isExpanded = expandedRows.has(rowKey);
                            const hasSnippet = r.responseSnippet && r.responseSnippet.length > 0;
                            const hasBaseline = r.baselineSnippet && r.baselineSnippet.length > 0;
                            const matchesSearch = searchLower && (
                              (hasSnippet && r.responseSnippet.toLowerCase().includes(searchLower)) ||
                              (hasBaseline && r.baselineSnippet.toLowerCase().includes(searchLower))
                            );
                            const sizeDiff = hasBaseline && r.responseLength != null
                              ? r.responseLength - r.baselineResponseLength
                              : null;
                            return (
                              <React.Fragment key={rowKey}>
                                <tr
                                  className={`border-t ${borderColor} cursor-pointer hover:${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'} ${
                                    isVulnerable ? isDarkMode ? 'bg-red-900/20' : 'bg-red-50'
                                    : matchesSearch ? isDarkMode ? 'bg-yellow-900/20' : 'bg-yellow-50'
                                    : ''
                                  }`}
                                  onClick={() => setExpandedRows(prev => {
                                    const next = new Set(prev);
                                    next.has(rowKey) ? next.delete(rowKey) : next.add(rowKey);
                                    return next;
                                  })}
                                  title={vd.desc}
                                >
                                  <td className={`px-2 py-1.5 font-mono ${textPrimary}`}>
                                    <span className="mr-1 text-[9px]">{isExpanded ? '▼' : '▶'}</span>
                                    SR={r.controlId}
                                  </td>
                                  <td className={`px-2 py-1.5 ${textSecondary}`}>
                                    {EVENT_ICONS[r.eventType] && <FontAwesomeIcon icon={EVENT_ICONS[r.eventType]} className="mr-1" />}
                                    {r.eventType}
                                    {r.txValue ? ` "${r.txValue.substring(0, 20)}"` : ''}
                                  </td>
                                  <td className={`px-2 py-1.5 ${textSecondary}`}>{r.responseStatus || '—'}</td>
                                  <td className={`px-2 py-1.5 text-right font-mono ${textSecondary}`}>
                                    {r.baselineResponseLength != null ? r.baselineResponseLength.toLocaleString() : '—'}
                                  </td>
                                  <td className={`px-2 py-1.5 text-right font-mono ${
                                    sizeDiff != null && sizeDiff < -500 ? 'text-green-500 font-bold'
                                    : sizeDiff != null && Math.abs(sizeDiff) < 50 ? (isDarkMode ? 'text-red-400' : 'text-red-600') + ' font-bold'
                                    : textSecondary
                                  }`}>
                                    {r.responseLength != null ? r.responseLength.toLocaleString() : '—'}
                                  </td>
                                  <td className={`px-2 py-1.5 text-right font-mono ${textSecondary}`}>
                                    {r.newControlCount != null ? r.newControlCount : '—'}
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${vd.bg} ${vd.text}`} title={vd.desc}>
                                      {isVulnerable && <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />}
                                      {r.verdict === 'IGNORED' && <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />}
                                      {vd.label}
                                    </span>
                                  </td>
                                </tr>
                                {isExpanded && (() => {
                                  const diff_ = (hasBaseline && hasSnippet) ? computeVwgDiff(r.baselineSnippet, r.responseSnippet) : null;
                                  const viewKey = `view-${rowKey}`;
                                  const currentView = expandedRows.has(`diff-${rowKey}`) ? 'diff' : 'side';
                                  const preClass = `text-[10px] font-mono whitespace-pre-wrap break-all max-h-64 overflow-auto p-2 rounded ${isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-100 text-gray-800'}`;
                                  return (
                                  <tr className={`border-t ${borderColor}`}>
                                    <td colSpan={7} className="px-2 py-2">
                                      <div className="flex items-center justify-between mb-1">
                                        <div className={`text-[10px] ${textSecondary}`}>{vd.desc}</div>
                                        {hasBaseline && hasSnippet && (
                                          <div className="flex gap-1">
                                            <button
                                              onClick={e => { e.stopPropagation(); setExpandedRows(prev => { const n = new Set(prev); n.delete(`diff-${rowKey}`); return n; }); }}
                                              className={`text-[10px] px-2 py-0.5 rounded ${currentView === 'side' ? 'bg-teal-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'}`}
                                            >Side-by-Side</button>
                                            <button
                                              onClick={e => { e.stopPropagation(); setExpandedRows(prev => { const n = new Set(prev); n.add(`diff-${rowKey}`); return n; }); }}
                                              className={`text-[10px] px-2 py-0.5 rounded ${currentView === 'diff' ? 'bg-teal-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'}`}
                                            >Diff</button>
                                          </div>
                                        )}
                                      </div>
                                      {r.error && <div className="text-[10px] text-red-400 mb-1">Error: {r.error}</div>}

                                      {currentView === 'diff' && diff_ ? (
                                        <div>
                                          <div className={`text-[10px] mb-2 ${textSecondary}`}>
                                            {diff_.shared} gemeinsame Zeilen, <span className={isDarkMode ? 'text-orange-400' : 'text-orange-600'}>{diff_.adminOnly.length} nur Admin</span>, <span className={isDarkMode ? 'text-teal-400' : 'text-teal-600'}>{diff_.lowprivOnly.length} nur Low-Priv</span>
                                          </div>
                                          {diff_.adminOnly.length > 0 && (
                                            <div className="mb-2">
                                              <div className={`text-[10px] font-bold mb-1 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>Nur in Admin ({diff_.adminOnly.length} Zeilen)</div>
                                              <pre className={`${preClass} ${isDarkMode ? 'border-l-2 border-orange-500' : 'border-l-2 border-orange-400 bg-orange-50'}`}>
                                                {diff_.adminOnly.map(d => d.text).join('\n')}
                                              </pre>
                                            </div>
                                          )}
                                          {diff_.lowprivOnly.length > 0 && (
                                            <div>
                                              <div className={`text-[10px] font-bold mb-1 ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}>Nur in Low-Priv ({diff_.lowprivOnly.length} Zeilen)</div>
                                              <pre className={`${preClass} ${isDarkMode ? 'border-l-2 border-teal-500' : 'border-l-2 border-teal-400 bg-teal-50'}`}>
                                                {diff_.lowprivOnly.map(d => d.text).join('\n')}
                                              </pre>
                                            </div>
                                          )}
                                          {diff_.adminOnly.length === 0 && diff_.lowprivOnly.length === 0 && (
                                            <div className={`text-[10px] ${textSecondary} italic`}>Responses sind identisch</div>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="grid grid-cols-2 gap-2">
                                          <div>
                                            <div className={`text-[10px] font-bold mb-1 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                                              Admin ({r.baselineResponseLength != null ? r.baselineResponseLength.toLocaleString() + 'B' : '—'})
                                            </div>
                                            {hasBaseline ? (
                                              <pre className={preClass}>{r.baselineSnippet}</pre>
                                            ) : (
                                              <span className={`text-[10px] ${textSecondary} italic`}>Keine Baseline-Response</span>
                                            )}
                                          </div>
                                          <div>
                                            <div className={`text-[10px] font-bold mb-1 ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}>
                                              Low-Priv ({r.responseLength != null ? r.responseLength.toLocaleString() + 'B' : '—'})
                                            </div>
                                            {hasSnippet ? (
                                              <pre className={preClass}>{r.responseSnippet}</pre>
                                            ) : (
                                              <span className={`text-[10px] ${textSecondary} italic`}>Keine Response</span>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                  );
                                })()}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </>
          )}

          {diff && diff.baselineOnly.length === 0 && (diff.shared || []).length === 0 && (
            <div className={`text-center py-6 ${textSecondary}`}>
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-2xl mb-2" />
              <p>Keine Controls gefunden — beiden Sessions fehlen verwertbare Events.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VwgAuthzTester;
